import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, Paper, IconButton, Tooltip, Divider, useTheme, useMediaQuery 
} from '@mui/material';
import { 
    Draw as DrawIcon, 
    CardGiftcard as BlindBagIcon, 
    Casino as CasinoIcon, 
    HourglassBottom as TimerIcon, 
    ChevronRight as ChevronRightIcon, 
    ChevronLeft as ChevronLeftIcon,
    DragIndicator as DragIndicatorIcon,
    Handyman as ToolkitIcon,
    SwapHoriz as SwapSideIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toggleTimer } from '../../redux/slices/timerSlice';
import { RootState } from '../../redux/store';
import LuckyWheelModal, { CallingStudent, getStoredCallingItems } from '../ClassSessions/LuckyWheelModal';
import { useIsImmersiveFocusMode } from '../../utils/immersiveMode';

const COLLAPSED_STORAGE_KEY = 'global_toolkit_collapsed';
const TOP_STORAGE_KEY = 'global_toolkit_top';
const SIDE_STORAGE_KEY = 'global_toolkit_side';

const GlobalToolkitBar: React.FC = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTimerOpen = useSelector((state: RootState) => state.timer?.isOpen ?? false);

    const user = useSelector((state: RootState) => state.auth?.user);
    const [pathname, setPathname] = useState(typeof window !== 'undefined' ? window.location.pathname : '');
    const isImmersiveFocus = useIsImmersiveFocusMode();

    useEffect(() => {
        const updatePath = () => setPathname(window.location.pathname);
        window.addEventListener('popstate', updatePath);
        window.addEventListener('tgo-route-change', updatePath);
        const interval = setInterval(updatePath, 500);
        return () => {
            window.removeEventListener('popstate', updatePath);
            window.removeEventListener('tgo-route-change', updatePath);
            clearInterval(interval);
        };
    }, []);

    const isLessonDetailPage = pathname.includes('/lessons/');

    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
    });

    // Default to 'left' side on mobile or when in lesson detail page so it doesn't overlap the right tab strip
    const [dockSide, setDockSide] = useState<'left' | 'right'>(() => {
        const saved = localStorage.getItem(SIDE_STORAGE_KEY);
        if (saved === 'left' || saved === 'right') return saved;
        if (typeof window !== 'undefined' && (window.innerWidth < 900 || window.location.pathname.includes('/lessons/'))) {
            return 'left';
        }
        return 'right';
    });

    const [topPosition, setTopPosition] = useState<number>(() => {
        const saved = localStorage.getItem(TOP_STORAGE_KEY);
        if (saved) {
            const num = parseInt(saved, 10);
            if (!isNaN(num) && num > 50 && num < window.innerHeight - 100) {
                return num;
            }
        }
        return Math.max(100, Math.round(window.innerHeight * 0.35));
    });

    const [luckyWheelOpen, setLuckyWheelOpen] = useState(false);
    const [luckyWheelMode, setLuckyWheelMode] = useState<'wheel' | 'blindbag'>('wheel');
    const [callingItems, setCallingItems] = useState<CallingStudent[]>(getStoredCallingItems);

    const isDraggingRef = useRef(false);
    const startYRef = useRef(0);
    const startXRef = useRef(0);
    const lastXRef = useRef(0);
    const startTopRef = useRef(0);

    // If on mobile / lesson detail and user hasn't explicitly set preference, keep on left
    useEffect(() => {
        const saved = localStorage.getItem(SIDE_STORAGE_KEY);
        if (!saved && (isMobile || isLessonDetailPage)) {
            setDockSide('left');
        }
    }, [isMobile, isLessonDetailPage]);

    const handleDragStart = (clientX: number, clientY: number) => {
        isDraggingRef.current = true;
        startYRef.current = clientY;
        startXRef.current = clientX;
        lastXRef.current = clientX;
        startTopRef.current = topPosition;
        document.body.style.userSelect = 'none';
    };

    const handleDragMove = (clientX: number, clientY: number) => {
        if (!isDraggingRef.current) return;
        lastXRef.current = clientX;
        const deltaY = clientY - startYRef.current;
        const maxTop = window.innerHeight - 220;
        const newTop = Math.max(70, Math.min(maxTop, startTopRef.current + deltaY));
        setTopPosition(newTop);
    };

    const handleDragEnd = () => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        document.body.style.userSelect = '';
        localStorage.setItem(TOP_STORAGE_KEY, String(topPosition));

        // If dragged horizontally past middle of screen, snap to corresponding side
        const deltaX = Math.abs(lastXRef.current - startXRef.current);
        if (deltaX > 80) {
            const newSide = lastXRef.current < window.innerWidth / 2 ? 'left' : 'right';
            setDockSide(newSide);
            localStorage.setItem(SIDE_STORAGE_KEY, newSide);
        }
    };

    // Mouse drag handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        handleDragStart(e.clientX, e.clientY);

        const onMouseMove = (moveEvent: MouseEvent) => {
            handleDragMove(moveEvent.clientX, moveEvent.clientY);
        };

        const onMouseUp = () => {
            handleDragEnd();
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    // Touch drag handlers for mobile devices
    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length !== 1) return;
        handleDragStart(e.touches[0].clientX, e.touches[0].clientY);

        const onTouchMove = (moveEvent: TouchEvent) => {
            if (moveEvent.touches.length !== 1) return;
            handleDragMove(moveEvent.touches[0].clientX, moveEvent.touches[0].clientY);
        };

        const onTouchEnd = () => {
            handleDragEnd();
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
        };

        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('touchend', onTouchEnd);
    };

    const toggleCollapse = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
    };

    const handleSwitchSide = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        const nextSide = dockSide === 'right' ? 'left' : 'right';
        setDockSide(nextSide);
        localStorage.setItem(SIDE_STORAGE_KEY, nextSide);
    };

    const handleOpenWhiteboard = () => {
        window.open('/teacher/whiteboard', '_blank');
    };

    const handleOpenBlindBag = () => {
        setLuckyWheelMode('blindbag');
        setLuckyWheelOpen(true);
    };

    const handleOpenLuckyWheel = () => {
        setLuckyWheelMode('wheel');
        setLuckyWheelOpen(true);
    };

    const handleToggleTimer = () => {
        dispatch(toggleTimer());
    };

    const authPaths = [
        '/login',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/verify-account',
        '/verify-email',
        '/organization/sign-up'
    ];
    
    const currentLoc = typeof window !== 'undefined' 
        ? ((window.location.pathname || '') + (window.location.hash || '') + (window.location.search || '')).toLowerCase() 
        : '';
    const isAuthPage = authPaths.some(p => pathname.toLowerCase().includes(p) || currentLoc.includes(p));
    const hasToken = typeof window !== 'undefined' && Boolean(localStorage.getItem('access_token'));

    // Don't render on Whiteboard PRO, Game Player screens, presentation phone remote view, unauthenticated sessions, or auth pages
    if (isImmersiveFocus || currentLoc.includes('/slides/remote') || isAuthPage || !user || !hasToken) {
        return null;
    }

    const isLeft = dockSide === 'left';
    const tooltipPlacement = isLeft ? 'right' : 'left';

    return (
        <>
            <Box
                className="global-toolkit-bar"
                id="global-toolkit-bar"
                sx={{
                    position: 'fixed',
                    left: isLeft ? 0 : 'auto',
                    right: isLeft ? 'auto' : 0,
                    top: topPosition,
                    zIndex: 1300,
                    display: 'flex',
                    alignItems: 'center',
                    transition: isDraggingRef.current ? 'none' : 'top 0.1s ease, left 0.2s ease, right 0.2s ease',
                    '@media print': {
                        display: 'none'
                    }
                }}
            >
                <Paper
                    elevation={6}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        borderTopLeftRadius: isLeft ? 0 : 16,
                        borderBottomLeftRadius: isLeft ? 0 : 16,
                        borderTopRightRadius: isLeft ? 16 : 0,
                        borderBottomRightRadius: isLeft ? 16 : 0,
                        border: '1px solid #e2e8f0',
                        borderLeft: isLeft ? 'none' : '1px solid #e2e8f0',
                        borderRight: isLeft ? '1px solid #e2e8f0' : 'none',
                        boxShadow: isLeft 
                            ? '5px 10px 25px -5px rgba(0, 0, 0, 0.15), 3px 8px 10px -6px rgba(0, 0, 0, 0.1)'
                            : '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        p: 0.5,
                        overflow: 'hidden'
                    }}
                >
                    {/* Drag Handle Header */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: '100%',
                            py: 0.3,
                            cursor: 'grab',
                            touchAction: 'none',
                            color: '#94a3b8',
                            '&:hover': { color: '#475569' },
                            '&:active': { cursor: 'grabbing' }
                        }}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                    >
                        <DragIndicatorIcon sx={{ fontSize: 16, transform: 'rotate(90deg)' }} />
                    </Box>

                    {/* Collapsed Pill Button */}
                    {isCollapsed ? (
                        <Tooltip title={`Mở công cụ trợ giảng (${isLeft ? 'Bên trái' : 'Bên phải'})`} placement={tooltipPlacement}>
                            <IconButton 
                                onClick={toggleCollapse}
                                sx={{ 
                                    width: isMobile ? 32 : 36, 
                                    height: isMobile ? 38 : 42, 
                                    borderRadius: isLeft ? '0 10px 10px 0' : '10px 0 0 10px',
                                    color: '#2563eb',
                                    bgcolor: 'rgba(37, 99, 235, 0.08)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.2,
                                    '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.16)' }
                                }}
                            >
                                <ToolkitIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                                {isLeft ? (
                                    <ChevronRightIcon sx={{ fontSize: 14 }} />
                                ) : (
                                    <ChevronLeftIcon sx={{ fontSize: 14 }} />
                                )}
                            </IconButton>
                        </Tooltip>
                    ) : (
                        /* Expanded Toolbar Tools */
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8, py: 0.5, px: 0.2 }}>
                            {/* Bảng trắng */}
                            <Tooltip title="Bảng trắng tương tác" placement={tooltipPlacement}>
                                <IconButton
                                    onClick={handleOpenWhiteboard}
                                    sx={{
                                        width: isMobile ? 34 : 38,
                                        height: isMobile ? 34 : 38,
                                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                                        color: '#059669',
                                        borderRadius: 2.5,
                                        transition: 'all 0.18s ease',
                                        '&:hover': {
                                            bgcolor: '#059669',
                                            color: '#fff',
                                            transform: 'scale(1.08)'
                                        }
                                    }}
                                >
                                    <DrawIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Xé túi mù */}
                            <Tooltip title="Xé túi mù 3D" placement={tooltipPlacement}>
                                <IconButton
                                    onClick={handleOpenBlindBag}
                                    sx={{
                                        width: isMobile ? 34 : 38,
                                        height: isMobile ? 34 : 38,
                                        bgcolor: 'rgba(236, 72, 153, 0.1)',
                                        color: '#db2777',
                                        borderRadius: 2.5,
                                        transition: 'all 0.18s ease',
                                        '&:hover': {
                                            bgcolor: '#db2777',
                                            color: '#fff',
                                            transform: 'scale(1.08)'
                                        }
                                    }}
                                >
                                    <BlindBagIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Vòng quay may mắn */}
                            <Tooltip title="Vòng quay may mắn" placement={tooltipPlacement}>
                                <IconButton
                                    onClick={handleOpenLuckyWheel}
                                    sx={{
                                        width: isMobile ? 34 : 38,
                                        height: isMobile ? 34 : 38,
                                        bgcolor: 'rgba(245, 158, 11, 0.1)',
                                        color: '#d97706',
                                        borderRadius: 2.5,
                                        transition: 'all 0.18s ease',
                                        '&:hover': {
                                            bgcolor: '#d97706',
                                            color: '#fff',
                                            transform: 'scale(1.08)'
                                        }
                                    }}
                                >
                                    <CasinoIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Đồng hồ đếm ngược */}
                            <Tooltip title={isTimerOpen ? "Ẩn đồng hồ đếm ngược" : "Bật đồng hồ đếm ngược"} placement={tooltipPlacement}>
                                <IconButton
                                    onClick={handleToggleTimer}
                                    sx={{
                                        width: isMobile ? 34 : 38,
                                        height: isMobile ? 34 : 38,
                                        bgcolor: isTimerOpen ? '#6366f1' : 'rgba(99, 102, 241, 0.1)',
                                        color: isTimerOpen ? '#fff' : '#6366f1',
                                        borderRadius: 2.5,
                                        transition: 'all 0.18s ease',
                                        '&:hover': {
                                            bgcolor: '#4f46e5',
                                            color: '#fff',
                                            transform: 'scale(1.08)'
                                        }
                                    }}
                                >
                                    <TimerIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                                </IconButton>
                            </Tooltip>

                            <Divider sx={{ width: '80%', my: 0.2 }} />

                            {/* Switch side button */}
                            <Tooltip title={isLeft ? "Chuyển sang mép phải màn hình" : "Chuyển sang mép trái màn hình"} placement={tooltipPlacement}>
                                <IconButton
                                    onClick={handleSwitchSide}
                                    size="small"
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        color: '#64748b',
                                        '&:hover': { color: '#2563eb', bgcolor: 'rgba(37, 99, 235, 0.08)' }
                                    }}
                                >
                                    <SwapSideIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Collapse button */}
                            <Tooltip title="Thu gọn thanh công cụ" placement={tooltipPlacement}>
                                <IconButton
                                    onClick={toggleCollapse}
                                    size="small"
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        color: '#94a3b8',
                                        '&:hover': { color: '#475569', bgcolor: 'rgba(0,0,0,0.04)' }
                                    }}
                                >
                                    {isLeft ? (
                                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                                    ) : (
                                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Global Lucky Wheel & Blind Bag Modal */}
            <LuckyWheelModal
                open={luckyWheelOpen}
                onClose={() => setLuckyWheelOpen(false)}
                students={callingItems}
                initialMode={luckyWheelMode}
                allowCustomList={true}
                onStudentsChange={(newItems) => setCallingItems(newItems)}
            />
        </>
    );
};

export default GlobalToolkitBar;
